// src/components/DetailsCard.js

import React from 'react';
import { Clock, Home, Users, AlertCircle } from 'lucide-react';

const DetailsCard = ({ expandedCard, data }) => {
  const formatDateTime = (datetime) => {
    return new Date(datetime).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const capitalizeWords = (str) => {
    return str
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  if (expandedCard === 'getitdone') {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Get It Done Reports Details</h3>
        </div>
        <div className="space-y-4 overflow-y-auto pr-2" style={{ maxHeight: '420px', scrollbarWidth: 'thin', scrollbarColor: '#94A3B8 #E2E8F0' }}>
          {data.incidents.map((incident) => (
            <div key={incident.service_request_id} className="border border-gray-100 rounded-lg p-3 hover:bg-gray-50">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium text-gray-900">{incident.service_name}</h4>
                  <p className="text-sm text-gray-600">
                    {incident.public_description || incident.service_name_detail || 'No description provided'}
                  </p>
                </div>
                <span className="text-xs text-gray-500 flex items-center whitespace-nowrap ml-2">
                  <Clock size={12} className="mr-1 flex-shrink-0" />
                  {formatDateTime(incident.date_requested)}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between text-xs">
                <span className="text-blue-600 bg-blue-50 px-2 py-1 rounded">
                  {incident.street_address || 'Location not provided'}
                </span>
                <span className="bg-yellow-50 text-yellow-600 px-2 py-1 rounded">
                  District {incident.council_district || 'Unknown'}
                </span>
              </div>
              {incident.status && (
                <div className="mt-2 text-xs">
                  <span className={`px-2 py-1 rounded ${
                    incident.status === 'New' ? 'bg-green-50 text-green-600' :
                    incident.status === 'In Process' ? 'bg-blue-50 text-blue-600' :
                    'bg-gray-50 text-gray-600'
                  }`}>
                    Status: {incident.status}
                  </span>
                </div>
              )}
            </div>
          ))}
          {data.incidents.length === 0 && (
            <p className="text-gray-600 text-center py-8">No reports available.</p>
          )}
        </div>
      </div>
    );
  }

  if (expandedCard === 'homelessness') {
    return (
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <Users className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Homeless Population Details</h3>
        </div>
        <div className="space-y-4">
          <p className="text-sm text-gray-700 mb-4">
            Total homeless persons in 2024: {data.homelessDetails.totalHomelessCount.toLocaleString()} ({data.homelessDetails.homelessChange > 0 ? '+' : ''}{data.homelessDetails.homelessChange}% over previous year)
          </p>

          <h4 className="text-md font-semibold mb-2 text-gray-800">Sheltered vs Unsheltered:</h4>
          <ul className="list-disc list-inside mb-4 text-sm text-gray-700">
            <li>Unsheltered: {data.homelessDetails.unshelteredCount.toLocaleString()} ({data.homelessDetails.unshelteredChange > 0 ? '+' : ''}{data.homelessDetails.unshelteredChange}%)</li>
            <li>Sheltered: {data.homelessDetails.shelteredCount.toLocaleString()} ({data.homelessDetails.shelteredChange > 0 ? '+' : ''}{data.homelessDetails.shelteredChange}%)</li>
          </ul>

          <h4 className="text-md font-semibold mb-2 text-gray-800">Demographics:</h4>
          <table className="min-w-full text-sm text-left mb-4">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-2 py-2 border-b">Category</th>
                <th className="px-2 py-2 border-b">Unsheltered %</th>
                <th className="px-2 py-2 border-b">Unsheltered Count</th>
                <th className="px-2 py-2 border-b">Sheltered %</th>
                <th className="px-2 py-2 border-b">Sheltered Count</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.homelessDetails.demographics).map(([key, value], index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-2 py-2 border-b capitalize">{key.replace(/([A-Z])/g, ' $1').trim()}</td>
                  <td className="px-2 py-2 border-b">{value.unshelteredPercent}%</td>
                  <td className="px-2 py-2 border-b">{value.unshelteredCount.toLocaleString()}</td>
                  <td className="px-2 py-2 border-b">{value.shelteredPercent}%</td>
                  <td className="px-2 py-2 border-b">{value.shelteredCount.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h4 className="text-md font-semibold mb-2 text-gray-800">Regional Breakdown:</h4>
          <table className="min-w-full text-sm text-left">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-2 py-2 border-b">Region</th>
                <th className="px-2 py-2 border-b">Percentage</th>
                <th className="px-2 py-2 border-b">Count</th>
              </tr>
            </thead>
            <tbody>
              {data.homelessDetails.regions.map((region, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-2 py-2 border-b">{region.name}</td>
                  <td className="px-2 py-2 border-b">{region.percent}%</td>
                  <td className="px-2 py-2 border-b">{region.count.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <p className="text-xs text-gray-500 mt-4">
            Source: <a href={data.homelessDetails.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
              {data.homelessDetails.source}
            </a>
          </p>
        </div>
      </div>
    );
  }

  if (expandedCard === 'evictions') {
    const evictionCurrentYear = 2022;
    const evictionCurrentCount = data.evictionData.unlawfulDetainerCases.find(e => e.year === evictionCurrentYear).count;
    const evictionPreviousCount = data.evictionData.unlawfulDetainerCases.find(e => e.year === evictionCurrentYear - 1).count;
    const evictionChange = ((evictionCurrentCount - evictionPreviousCount) / evictionPreviousCount) * 100;

    return (
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Eviction Trends Details</h3>
        </div>
        <div className="space-y-4">
          <p className="text-sm text-gray-700 mb-4">
            Total Unlawful Detainer Cases in {evictionCurrentYear}: {evictionCurrentCount.toLocaleString()} ({evictionChange > 0 ? '+' : ''}{evictionChange.toFixed(1)}% over previous year)
          </p>
          <h4 className="text-md font-semibold mb-2 text-gray-800">Yearly Eviction Data:</h4>
          <table className="min-w-full text-sm text-left table-fixed">
            <thead>
              <tr className="bg-gray-50">
                <th className="w-1/3 px-2 py-2 border-b">Year</th>
                <th className="w-1/3 px-2 py-2 border-b">Unlawful Detainer Cases</th>
                <th className="w-1/3 px-2 py-2 border-b">Sheriff Lockouts</th>
              </tr>
            </thead>
            <tbody>
              {data.evictionData.unlawfulDetainerCases.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="w-1/3 px-2 py-2 border-b">{item.year}</td>
                  <td className="w-1/3 px-2 py-2 border-b">{item.count.toLocaleString()}</td>
                  <td className="w-1/3 px-2 py-2 border-b">
                    {data.evictionData.sheriffLockouts[index].count.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-500 mt-4">
            Source: <a href="https://sdhc.org/wp-content/uploads/2024/01/SDHC_San-Diego-Eviction-Study_01112024.pdf" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
              Analysis of Residential Evictions in the City of San Diego, 2024
            </a>
          </p>
        </div>
      </div>
    );
  }

  if (expandedCard === 'averageRent') {
    const { bedrooms, medianRent, nationalAverageDifference, pricePerSqFt, totalRentals, source, asOfDate } = data.rentDetails;
    return (
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-100">
        <div className="flex items-center gap-2 mb-4">
          <Home className="h-5 w-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Rent Price Details</h3>
        </div>
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-gray-50 p-3 rounded">
              <div className="text-sm text-gray-500">Median Rent</div>
              <div className="text-lg font-semibold">${medianRent.toLocaleString()}</div>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <div className="text-sm text-gray-500">vs National Avg</div>
              <div className="text-lg font-semibold text-red-600">+{nationalAverageDifference}%</div>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <div className="text-sm text-gray-500">Price/Sq.Ft</div>
              <div className="text-lg font-semibold">${pricePerSqFt}</div>
            </div>
            <div className="bg-gray-50 p-3 rounded">
              <div className="text-sm text-gray-500">Total Listings</div>
              <div className="text-lg font-semibold">{totalRentals.toLocaleString()}</div>
            </div>
          </div>

          <div>
            <h4 className="font-medium text-gray-900 mb-3">Rent by Unit Type</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Unit Type</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Average Rent</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">30-Day Change</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year Change</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bedrooms.map((unit) => (
                    <tr key={unit.type} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{unit.type}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">${unit.averageRent.toLocaleString()}</td>
                      <td className={`px-4 py-3 text-sm ${unit.last30Days >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {unit.last30Days > 0 ? '+' : ''}{unit.last30Days}%
                      </td>
                      <td className={`px-4 py-3 text-sm ${unit.lastYear >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {unit.lastYear > 0 ? '+' : ''}{unit.lastYear}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <p className="text-xs text-gray-500 mt-4">
            Source: <a href={source} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">Zumper Rent Research - San Diego, CA</a>
            <br />
            Data as of: {asOfDate}
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default DetailsCard;
